/*
 * <variant-matrix> — Produkt-Variantentabelle
 * Auswahl per Checkbox (standardmäßig nichts ausgewählt). Pro-Zeile-Add sofort,
 * Sammel-Add der ausgewählten Zeilen über /cart/add.js (Multi-Item).
 * Live-Suche, Zwischensumme/Gesamtsumme. Wiederverwendung des bestehenden
 * Cart-Drawers (analog product-form.js).
 */
// Kaufmännisch auf `dec` Nachkommastellen runden. Nötig, weil toFixed() und ein
// rohes Math.round(x * 100) auf Double-Werten danebenliegen: (0.075).toFixed(2)
// === "0.07" und Math.round(1.005 * 100) === 100. toPrecision(12) glättet den
// Binärfehler vor dem Runden → 0.075→0.08, 1.005→1.01, 0.145→0.15. Deckungsgleich
// mit roundDec() in b2b-prices.js / b2b-card-prices.js, cgMoneyDec() in
// checkout-gate.js und DraftOrderBuilder::round($vp * $cf / $gf, 2) im Bestellpfad.
function vmRoundDec(n, dec) {
  if (window.SU) return window.SU.roundDec(n, dec);
  const d = (dec === undefined) ? 2 : dec;
  const x = parseFloat(n);
  if (!isFinite(x)) return x;
  const f = Math.pow(10, d);
  return Math.round(Number((x * f).toPrecision(12))) / f;
}

// Abgeleiteter Einheitspreis (Preis je Meter/Stück, Set-Stückpreis): shop-weit 3 Nachkomma-
// stellen. ⚠️ NICHT formatMoney() umbiegen — das formatiert auch Pack- und Positionspreise,
// die zweistellig bleiben (so rechnet auch der Server).
function vmUnitPrice(chf, q) {
  if (window.SU) return window.SU.unitPrice(chf, q);
  const dec = (q && q <= 1) ? 2 : 3;
  return vmRoundDec(chf, dec).toFixed(dec);
}

// Betrag (CHF) → ganze Rappen, binärfehlerfest.
// Pack- vs. Pro-Einheit-Entscheidung — EINE Wahrheit in assets/unit-price.js (window.SU).
// Fallback inline, falls das Asset (noch) nicht geladen ist.
function vmNormalize(rawChf, guestPackChf, q, opts) {
  if (window.SU) return window.SU.normalize(rawChf, guestPackChf, q, opts);
  const P = parseFloat(rawChf);
  const Q = parseFloat(q) || 0;
  if (opts && opts.isSet) return { perUnit: P, packPrice: P };
  if (!(Q > 1)) return { perUnit: P, packPrice: P };
  // Fallback-Spiegel von SU.normalize (unit-price.js) — beide Kriterien, siehe dort.
  if (guestPackChf > 0 && (P > guestPackChf / Math.sqrt(Q) || P * Q > guestPackChf * 1.25)) return { perUnit: P / Q, packPrice: P };
  return { perUnit: P, packPrice: P * Q };
}

function vmCents(chf) {
  const x = parseFloat(chf);
  return isFinite(x) ? Math.round(Number((x * 100).toPrecision(12))) : NaN;
}

if (!customElements.get('variant-matrix')) {
  customElements.define(
    'variant-matrix',
    class VariantMatrix extends HTMLElement {
      connectedCallback() {
        this.cartAddUrl =
          this.dataset.cartAddUrl || (window.routes && window.routes.cart_add_url) || '/cart/add';
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Daten-Insel defensiv lesen: sie trägt Fremdtext (Zubehörtitel, Beschreibungen).
        // Ein einziges kaputtes Zeichen legte hier früher die KOMPLETTE Matrix lahm —
        // lieber ohne Summen/Zubehör weiterlaufen als gar nicht.
        const dataEl = this.querySelector('.variant-matrix__data');
        this.data = { variants: {} };
        if (dataEl) {
          try {
            this.data = JSON.parse(dataEl.textContent);
          } catch (e) {
            console.warn('[variant-matrix] Daten-Insel unlesbar:', e);
          }
        }
        if (!this.data.variants) this.data.variants = {};
        if (!this.data.accCatalog) this.data.accCatalog = {};
        // Auswahl des Zubehör-Konfigurators: { hostVariantId: { accId: menge } }.
        // Bewusst hier und nicht im DOM — überlebt Öffnen/Schliessen, Sortieren und Filtern.
        this.cfgSel = {};

        this.errorEl = this.querySelector('.variant-matrix__error');
        // Gesamtsumme wird an mehreren Stellen angezeigt (oben über der Tabelle
        // und im Footer) — alle Vorkommen werden synchron aktualisiert.
        this.grandEls = Array.from(this.querySelectorAll('.variant-matrix__grand-value'));
        this.noResultsEl = this.querySelector('.variant-matrix__noresults');
        // Sammel-Button kann mehrfach vorkommen (oben über der Tabelle + im Footer)
        this.addAllBtns = Array.from(this.querySelectorAll('.vm-add-all'));
        this.selectAll = this.querySelector('.vm-select-all');

        // Mengenfelder auf ihr Minimum setzen (Snippet füllt sonst die aktuelle
        // Warenkorbmenge vor — hier zählt die *hinzuzufügende* Menge).
        this.querySelectorAll('.quantity__input').forEach((inp) => {
          const min = parseInt(inp.min, 10) || 1;
          inp.value = min;
        });

        // Per-Zeile Hinzufügen (unabhängig von der Checkbox)
        this.querySelectorAll('.vm-add').forEach((btn) =>
          btn.addEventListener('click', () => this.addSingle(btn))
        );

        // Sammel-Buttons (oben + Footer): nur ausgewählte Zeilen
        this.addAllBtns.forEach((btn) => btn.addEventListener('click', () => this.addSelected(btn)));

        // Alle auswählen — nur die Zeilen, die Suche/Filter gerade zeigen
        if (this.selectAll) {
          this.selectAll.addEventListener('change', () => {
            this.visibleBoxes().forEach((cb) => {
              cb.checked = this.selectAll.checked;
            });
            this.onSelectionChange();
          });
        }

        // Einzel-Checkboxen
        this.querySelectorAll('.vm-select').forEach((cb) =>
          cb.addEventListener('change', () => this.onSelectionChange())
        );

        // Zubehör-Haken (Cross-Sell): wählt die Host-Variante automatisch mit an
        // (damit das Produkt + Zubehör gemeinsam in den Warenkorb gehen), dann Summen neu.
        this.querySelectorAll('.vm-mp-check').forEach((cb) =>
          cb.addEventListener('change', () => {
            if (cb.checked) {
              const row = cb.closest('.variant-matrix__row');
              const sel = row && row.querySelector('.vm-select');
              if (sel && !sel.checked && !sel.disabled) sel.checked = true;
            }
            this.onSelectionChange();
          })
        );
        // Zubehör-Mengenwähler (editable, z. B. Seitenwand): Summen neu berechnen
        this.querySelectorAll('.vm-acc-qty').forEach((inp) => {
          const recompute = () => this.onSelectionChange();
          inp.addEventListener('change', recompute);
          inp.addEventListener('input', recompute);
          // Klick im Mengenfeld soll die Zeilenauswahl nicht togglen
          inp.addEventListener('click', (e) => e.stopPropagation());
        });

        // Zubehör-Namen öffnen das Info-Popup (Bild + Daten der jeweiligen Variante)
        this.initAccQuickView();
        this.initImgLightbox();

        // Live-Suche + Attribut-Filter (Dropdowns)
        this.searchTerm = '';
        this.filterSelects = Array.from(this.querySelectorAll('.vm-filter__select'));
        this.resetBtn = this.querySelector('.vm-filters__reset');
        const searchInput = this.querySelector('.variant-matrix__search-input');
        if (searchInput)
          searchInput.addEventListener('input', (e) => {
            this.searchTerm = e.target.value;
            this.applyFilters();
          });
        this.filterSelects.forEach((sel) => sel.addEventListener('change', () => this.applyFilters()));
        if (this.resetBtn) this.resetBtn.addEventListener('click', () => this.resetFilters());

        // Mengen live -> Variante automatisch auswählen + Summen aktualisieren
        this.addEventListener('change', (e) => {
          if (e.target.classList && e.target.classList.contains('quantity__input')) this.onQtyChange(e.target);
        });
        this.addEventListener('input', (e) => {
          if (e.target.classList && e.target.classList.contains('quantity__input')) this.onQtyChange(e.target);
        });

        // Varianten numerisch sortieren (200 vor 1000) — Tabellenzeilen + Filter-Dropdowns.
        // Liquid sortiert nur lexikalisch ("1000" < "200"); hier per Natural-Sort korrigiert.
        this.sortRowsNumerically();
        this.sortFilterOptions();
        this.syncCfgCounts();

        this.applyB2bPrices();
        this.onSelectionChange();
        this.focusVariantFromUrl();
      }

      // Aufgabe #67: Kommt der Kunde über die Suche mit ?variant=<id>, die Matrix auf genau
      // diese Zeile filtern — über die vorhandene Suche, damit "Zurücksetzen" wieder alle
      // zeigt. Ausgeblendete Zeilen (Preis 0, unveröffentlicht ohne Bestand) gibt es im DOM
      // nicht; dann bleibt die Matrix unverändert.
      focusVariantFromUrl() {
        const id = new URLSearchParams(window.location.search).get('variant');
        if (!id || !/^\d+$/.test(id)) return;
        const row = this.querySelector(`.variant-matrix__row[data-variant-id="${id}"]`);
        const sku = row && row.dataset.sku;
        if (!sku) return;
        const searchInput = this.querySelector('.variant-matrix__search-input');
        if (searchInput) searchInput.value = sku;
        this.filterRows(sku.toLowerCase());
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      // ---- Dialog-Stapel ----------------------------------------------------
      // Der Konfigurator und die Produktvorschau können übereinander liegen. Ohne Stapel
      // schloss Escape beide, und closeAccModal() gab den Seiten-Scroll frei, obwohl der
      // Konfigurator noch offen war. Fokus wandert beim Schliessen dorthin zurück, wo er herkam.
      pushModal(el) {
        if (!el) return;
        this._stack = this._stack || [];
        const prev = this._stack[this._stack.length - 1];
        if (prev) {
          prev.el.setAttribute('inert', '');
          prev.el.setAttribute('aria-hidden', 'true');
          prev.el.classList.add('vm-modal--behind'); // Fallback für Browser ohne inert
        }
        this._stack.push({ el: el, prevFocus: document.activeElement });
        el.hidden = false;
        if (this._stack.length === 1) document.body.classList.add('overflow-hidden');
        const dlg = el.querySelector('[role="dialog"]') || el;
        if (dlg.focus) dlg.focus();
        this.bindModalKeys();
      }

      popModal(el) {
        this._stack = this._stack || [];
        const top = this._stack[this._stack.length - 1];
        if (!top || (el && top.el !== el)) return;
        this._stack.pop();
        top.el.hidden = true;
        const prev = this._stack[this._stack.length - 1];
        if (prev) {
          prev.el.removeAttribute('inert');
          prev.el.removeAttribute('aria-hidden');
          prev.el.classList.remove('vm-modal--behind');
        } else {
          document.body.classList.remove('overflow-hidden');
        }
        if (top.prevFocus && top.prevFocus.focus) top.prevFocus.focus();
      }

      // Ein einziger Tastatur-Handler für den gesamten Stapel (früher registrierte jede
      // Matrix-Instanz einen eigenen, nie entfernten Escape-Listener auf document).
      bindModalKeys() {
        if (this._escBound) return;
        this._escBound = true;
        document.addEventListener('keydown', (e) => {
          const stack = this._stack || [];
          const top = stack[stack.length - 1];
          if (!top) return;
          if (e.key === 'Escape') {
            e.stopPropagation();
            if (top.el === this.imgModal) this.closeImgModal();
            else if (top.el === this.accModal) this.closeAccModal();
            else if (this.cfg && top.el === this.cfg.root) this.cfg.close();
            else this.popModal(top.el);

            return;
          }
          if (e.key !== 'Tab') return;
          // Fokus im obersten Dialog halten.
          const f = top.el.querySelectorAll(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
          );
          const items = Array.from(f).filter((el) => el.offsetParent !== null);
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        });
      }

      // ---- Grossansicht Variantenbild (17.08.2026) --------------------------
      // Klick auf das Mini-Bild in der Bildspalte oeffnet das Vollbild. Delegiert am
      // Custom Element statt je Button, damit auch nachgeladene/umsortierte Zeilen greifen
      // (sortRowsNumerically() haengt die <tr> neu ein).
      initImgLightbox() {
        this.imgModal = this.querySelector('.vm-img-modal');
        if (!this.imgModal) return;
        this.imgModalImg = this.imgModal.querySelector('.vm-img-modal__img');
        this.imgModalCaption = this.imgModal.querySelector('.vm-img-modal__caption');
        // Beschriftung für die per JS erzeugten Ausloeser (Info-Popup); im Markup steht sie
        // bereits als aria-label an den Thumbnails der Bildspalte.
        const zt = this.querySelector('.vm-cfg-modal');
        this.zoomLabel = (zt && zt.dataset.i18nZoom) || '';
        this.imgModal.querySelectorAll('[data-vmimg-close]').forEach((el) =>
          el.addEventListener('click', () => this.closeImgModal())
        );
        this.bindModalKeys();

        this.addEventListener('click', (e) => {
          // `.vm-thumb-btn` ist die Bildspalte der Tabelle; `[data-vmimg]` sind die später per JS
          // erzeugten Ausloeser im Konfigurator und im Zubehör-Info-Popup. Beide liegen innerhalb
          // von <variant-matrix>, deshalb reicht dieser eine delegierte Handler (24.08.2026).
          const btn = e.target.closest('.vm-thumb-btn, [data-vmimg]');
          if (!btn || !this.contains(btn)) return;
          e.preventDefault();
          // Die Bildzelle liegt in einer Zeile mit eigenem Klick-/Auswahlverhalten —
          // ohne stopPropagation() wuerde der Klick zusaetzlich die Zeile selektieren.
          e.stopPropagation();
          this.openImgModal(btn);
        });
      }

      // Grosse Fassung einer Bild-URL: der Detailpool führt 900 px, die Grossansicht will mehr.
      // Greift die Ersetzung nicht, bleibt die Originalfassung stehen.
      bigImgUrl(url) {
        if (!url) return url;

        return /([?&])width=\d+/.test(url) ? url.replace(/([?&])width=\d+/, '$1width=1600') : url;
      }

      openImgModal(btn) {
        const src = btn.getAttribute('data-vmimg');
        if (!src || !this.imgModalImg) return;
        const title = btn.getAttribute('data-vmimg-title') || '';
        this.imgModalImg.src = src;
        this.imgModalImg.alt = title;
        if (this.imgModalCaption) this.imgModalCaption.textContent = title;
        this.pushModal(this.imgModal);
      }

      closeImgModal() {
        if (!this.imgModal || this.imgModal.hidden) return;
        this.popModal(this.imgModal);
        // src leeren: das naechste Oeffnen soll nicht kurz das vorige Bild zeigen.
        if (this.imgModalImg) this.imgModalImg.removeAttribute('src');
      }

      // ---- Zubehör-Info-Popup (Klick auf Namen) -----------------------------
      // Öffnet ein Modal mit Bild (variantenspezifisch/Produkt-Fallback) + Daten
      // des jeweiligen Zubehörs; klont den Inhalt des zugehörigen .vm-acc-detail.
      initAccQuickView() {
        this.accModal = this.querySelector('.vm-acc-modal');
        if (this.accModal) {
          this.accModalTitle = this.accModal.querySelector('.vm-acc-modal__title');
          this.accModalBody = this.accModal.querySelector('.vm-acc-modal__body');
          this.accModalMedia = this.accModal.querySelector('.vm-acc-modal__media');
          this.accModalGrid = this.accModal.querySelector('.vm-acc-modal__grid');
          this.accModalLink = this.accModal.querySelector('.vm-acc-modal__link');
          this.accModal.querySelectorAll('[data-vmacc-close]').forEach((el) =>
            el.addEventListener('click', () => this.closeAccModal())
          );
          this.bindModalKeys();
        }
        // Delegiert statt pro Element: die Zubehörzeilen des Konfigurators entstehen erst
        // beim Öffnen, ein Listener je Button würde sie nie erreichen.
        this.addEventListener('click', (e) => {
          const qv = e.target.closest('.vm-acc-qv');
          if (qv && this.contains(qv)) {
            e.preventDefault();
            e.stopPropagation();
            if (this.accModal) this.openAccModal(qv);

            return;
          }
          const open = e.target.closest('.vm-cfg-open');
          if (open && this.contains(open)) {
            e.preventDefault();
            this.openConfigurator(open.dataset.variantId);
          }
        });
      }

      // Zubehör-Schlüssel dieser Zeile: erst das variantenspezifische, dann das produktweite.
      // ⚠️ Entdopplung über die Produkt-ID: die variantenspezifischen Einträge sind oft eine
      // BESTIMMTE Ausführung genau jener Produkte, die auch produktweit gelistet sind
      // (z. B. „SWG Höhenprofil innen 600 mm" vs. dasselbe Produkt mit Längenauswahl).
      // Dann gewinnt die präzise Variantenzuordnung, sonst stünde das Zubehör doppelt im Dialog.
      cfgKeysFor(hostId) {
        const cat = this.data.accCatalog || {};
        const v = this.data.variants[hostId] || {};
        const out = [];
        const pids = {};
        const seen = {};
        // Reihenfolge = Genauigkeit: Fixslot der Zeile ▸ variantenspezifisch ▸ produktweit.
        //
        // ⚠️ Die pid-Entdopplung wirkt NUR über diese Stufen hinweg, nicht innerhalb einer Stufe
        // (korrigiert 11.08.2026). Sie soll verhindern, dass dasselbe Produkt einmal präzise
        // zugeordnet und einmal produktweit erscheint. Innerhalb der variantenspezifischen Liste
        // sind mehrere Einträge desselben Produkts dagegen ABSICHT: das sind verschiedene
        // Ausführungen, zwischen denen die Kundschaft wählt. Vorher fielen sie still weg — an der
        // SWG Adapterplatte blieb von drei hinterlegten Kabeldurchführungsplatten (F 17-2, F 17-3,
        // F 30-1) nur die erste übrig, und der Zähler zeigte entsprechend "(1)" statt "(3)".
        const addStage = (keys) => {
          const fromEarlierStages = Object.assign({}, pids);
          (keys || []).forEach((k) => {
            const a = cat[k];
            if (!a || seen[k] || fromEarlierStages[a.pid]) return;
            seen[k] = true;
            pids[a.pid] = true;
            out.push(k);
          });
        };
        addStage(Object.keys(v.slots || {}).map((s) => v.slots[s]));
        addStage(v.cfg);
        addStage(this.data.accProduct);

        return out;
      }

      // Zähler am Auslöser exakt setzen. Liquid kennt beim Rendern der Zeile die Zahl nur
      // grob (Zubehör ohne bepreiste Ausführung fällt erst beim Aufbau des Katalogs heraus);
      // hier steht die tatsächliche Zahl. Zeilen ohne jedes Zubehör verlieren den Auslöser.
      syncCfgCounts() {
        this.querySelectorAll('.vm-cfg-open').forEach((btn) => {
          const n = this.cfgKeysFor(btn.dataset.variantId).length;
          const trigger = btn.closest('.vm-cfg-trigger') || btn;
          if (!n) {
            trigger.hidden = true;

            return;
          }
          const c = btn.querySelector('.vm-cfg-open__count');
          if (c) c.textContent = '(' + n + ')';
        });
      }

      // Konfigurator erst beim ersten Klick instanziieren — dann ist die Ladereihenfolge
      // der beiden defer-Skripte egal, und wer ihn nie öffnet, zahlt keine Aufbaukosten.
      openConfigurator(hostId) {
        if (!hostId) return;
        if (!this.cfg) {
          if (!window.VmAccConfigurator) return;
          this.cfg = new window.VmAccConfigurator(this);
          if (!this.cfg.root) {
            this.cfg = null;

            return;
          }
        }
        this.cfg.open(hostId);
      }

      // Zubehör-Details werden erst beim Öffnen geholt (11.08.2026): sie inline zu rendern kostete
      // auf der Stahl-Wandgehäuse-Seite ~1.790 ms von 2.207 ms Render-Zeit und trieb sie in
      // Shopifys Render-Limit (HTTP 500). Quelle ist sections/vm-acc-detail.liquid, die URL steht
      // fertig in data-lazy. Das Ergebnis landet im Pool-Knoten selbst — damit ist jedes Zubehör
      // genau einmal je Seitenaufruf zu holen, und alle bestehenden Leser sehen es unverändert.
      loadAccDetail(detail) {
        const url = detail.dataset.lazy;
        if (!url) return Promise.resolve();
        if (detail.dataset.lazyState === 'done') return Promise.resolve();
        if (detail._lazyPromise) return detail._lazyPromise;

        detail._lazyPromise = fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then((r) => (r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status))))
          .then((html) => {
            // Die Section Rendering API liefert den Section-Wrapper mit; gebraucht wird nur sein Inhalt.
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const inner = doc.querySelector('.shopify-section') || doc.body;
            detail.innerHTML = inner ? inner.innerHTML.trim() : '';
            detail.dataset.lazyState = 'done';
          })
          .catch(() => {
            // Fehlschlag darf die Vorschau nicht blockieren: Bild, Titel und Produktlink stehen
            // ohnehin im Modal. Nicht als 'done' markieren, damit der nächste Versuch neu lädt.
            detail.innerHTML = '';
            detail._lazyPromise = null;
          });

        return detail._lazyPromise;
      }

      openAccModal(btn) {
        const detail = this.querySelector(btn.getAttribute('data-detail') || '');
        if (!detail) return;
        this.accModalTitle.textContent = detail.getAttribute('data-title') || btn.textContent.trim();
        if (detail.dataset.lazy && detail.dataset.lazyState !== 'done') {
          this.accModalBody.innerHTML =
            '<div class="vm-acc-detail__loading" role="status">' +
            (this.accModal.dataset.loadingLabel || '…') +
            '</div>';
          this.loadAccDetail(detail).then(() => {
            // Nur übernehmen, wenn inzwischen kein anderes Zubehör geöffnet wurde.
            if (this.accModalOpenKey === detail.id) this.accModalBody.innerHTML = detail.innerHTML;
          });
        } else {
          this.accModalBody.innerHTML = detail.innerHTML;
        }
        this.accModalOpenKey = detail.id;
        if (this.accModalLink) this.accModalLink.setAttribute('href', detail.getAttribute('data-url') || '#');
        const img = detail.getAttribute('data-image');
        if (this.accModalMedia) {
          this.accModalMedia.innerHTML = '';
          this.accModalMedia.onmousemove = this.accModalMedia.onmouseleave = null;
          if (img) {
            const im = document.createElement('img');
            im.src = img;
            im.alt = this.accModalTitle.textContent;
            // Klick öffnet die Grossansicht (.vm-img-modal, liegt im selben Custom Element).
            // ⚠️ Button statt Klick-Handler am <img>: sonst nicht per Tastatur erreichbar.
            // Der Hover-Zoom hängt an .vm-acc-modal__media und läuft über den Button hinweg weiter.
            const zoomBtn = document.createElement('button');
            zoomBtn.type = 'button';
            zoomBtn.className = 'vm-acc-modal__zoom';
            zoomBtn.setAttribute('data-vmimg', this.bigImgUrl(img));
            zoomBtn.setAttribute('data-vmimg-title', this.accModalTitle.textContent);
            zoomBtn.setAttribute(
              'aria-label',
              (this.zoomLabel ? this.zoomLabel + ': ' : '') + this.accModalTitle.textContent
            );
            zoomBtn.appendChild(im);
            this.accModalMedia.appendChild(zoomBtn);
            if (this.accModalGrid) this.accModalGrid.classList.remove('is-single');
            const ZOOM = 2.2;
            this.accModalMedia.onmousemove = (ev) => {
              const r = im.getBoundingClientRect();
              const x = Math.min(100, Math.max(0, ((ev.clientX - r.left) / r.width) * 100));
              const y = Math.min(100, Math.max(0, ((ev.clientY - r.top) / r.height) * 100));
              im.style.transformOrigin = x + '% ' + y + '%';
              im.style.transform = 'scale(' + ZOOM + ')';
            };
            this.accModalMedia.onmouseleave = () => { im.style.transform = ''; };
          } else if (this.accModalGrid) {
            this.accModalGrid.classList.add('is-single');
          }
        }
        this.pushModal(this.accModal);
        this.accModalBody.scrollTop = 0;
      }

      closeAccModal() {
        if (!this.accModal || this.accModal.hidden) return;
        this.popModal(this.accModal);
      }

      // ---- Numerische/natürliche Sortierung (2026-06-25) --------------------
      // Vergleicht zwei Strings "natürlich": Ziffernblöcke werden als Zahlen
      // verglichen, sodass 200 vor 1000 steht und z. B. "200x400" vor "1000x400".
      // Nicht-numerische Namen bleiben alphabetisch (case-insensitiv).
      naturalCompare(a, b) {
        const chunk = (s) =>
          String(s == null ? '' : s)
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim()
            .match(/(\d+(?:[.,]\d+)?|\D+)/g) || [];
        const ca = chunk(a);
        const cb = chunk(b);
        const len = Math.min(ca.length, cb.length);
        for (let i = 0; i < len; i++) {
          const na = parseFloat(ca[i].replace(',', '.'));
          const nb = parseFloat(cb[i].replace(',', '.'));
          const bothNum = !Number.isNaN(na) && /\d/.test(ca[i]) && !Number.isNaN(nb) && /\d/.test(cb[i]);
          if (bothNum) {
            if (na !== nb) return na - nb;
          } else if (ca[i] !== cb[i]) {
            return ca[i] < cb[i] ? -1 : 1;
          }
        }
        return ca.length - cb.length;
      }

      // Konfektionsgrössen (2026-08-10). Alphabetisch stünde L vor M vor S vor XL —
      // fachlich falsch. Rang statt Alphabet, aber NUR wenn beide Labels für sich
      // genommen exakt eine Grösse sind; jede andere Bezeichnung fällt unverändert
      // in naturalCompare zurück, damit bestehende Matrizen unberührt bleiben.
      sizeRank(s) {
        const key = String(s == null ? '' : s).trim().toLowerCase().replace(/\s+/g, '');
        return ['xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', '2xl', 'xxxl', '3xl'].indexOf(key);
      }

      // Vergleich für Zeilennamen und Filter-Optionen: Grössen nach Rang, sonst natürlich.
      labelCompare(a, b) {
        const ra = this.sizeRank(a);
        const rb = this.sizeRank(b);
        if (ra >= 0 && rb >= 0 && ra !== rb) return ra - rb;
        return this.naturalCompare(a, b);
      }

      // Tabellenzeilen im tbody neu anordnen. Bringt JEDE Zeile einen numerischen
      // Sortier-Schlüssel mit (data-sort1 = Querschnitt, data-sort2 = Länge, gesetzt in
      // variant-matrix.liquid), wird danach sortiert — sonst wie bisher nach Variantenname
      // (.vm-name). DOM-Umordnung erhält Event-Listener; Filter/Suche/B2B-Preise unverändert.
      sortRowsNumerically() {
        const rows = Array.from(this.querySelectorAll('.variant-matrix__row'));
        if (rows.length < 2) return;
        const tbody = rows[0].parentNode;
        if (!tbody) return;
        const keyOf = (row) => {
          const nameEl = row.querySelector('.vm-name');
          return nameEl ? nameEl.textContent : row.getAttribute('data-search') || '';
        };
        const num = (row, attr) => {
          const n = parseFloat((row.getAttribute(attr) || '').replace(',', '.'));
          return Number.isNaN(n) ? null : n;
        };
        const bySpec = rows.every((r) => num(r, 'data-sort1') !== null);
        const cmp = bySpec
          ? (r1, r2) => {
              const d1 = num(r1, 'data-sort1') - num(r2, 'data-sort1');
              if (d1) return d1;
              const s1 = num(r1, 'data-sort2');
              const s2 = num(r2, 'data-sort2');
              if (s1 !== null && s2 !== null && s1 !== s2) return s1 - s2;
              return this.labelCompare(keyOf(r1), keyOf(r2));
            }
          : (r1, r2) => this.labelCompare(keyOf(r1), keyOf(r2));
        rows
          .slice()
          .sort(cmp)
          .forEach((row) => tbody.appendChild(row));
      }

      // Optionen jedes Filter-Dropdowns natürlich sortieren; Platzhalter
      // (leerer Wert, "Alle") bleibt immer an erster Stelle.
      sortFilterOptions() {
        this.querySelectorAll('.vm-filter__select').forEach((sel) => {
          const opts = Array.from(sel.options);
          const placeholders = opts.filter((o) => o.value === '');
          const values = opts.filter((o) => o.value !== '');
          if (values.length < 2) return;
          values.sort((a, b) => this.labelCompare(a.textContent, b.textContent));
          placeholders.concat(values).forEach((o) => sel.appendChild(o));
        });
      }

      // ---- B2B-Individualpreise (Block INDIV-PRICE-1, 2026-06-23) -----------
      // Holt kundenindividuelle Preise (FindProductPrice via /api/b2b/customer-prices)
      // und überschreibt Einzelpreis-Zelle + data.variants[id].price (Basis für recalc).
      // No-op für Nicht-B2B-Besucher; Fehler blockieren die Matrix nie.
      applyB2bPrices() {
        if (this._b2bApplied) return;            // idempotent: nur einmal pro Instanz
        const cfg = window.b2bConfig;
        if (!cfg || !cfg.isB2B || !cfg.email || !cfg.apiKey || !cfg.apiBase) return;
        const variants = (this.data && this.data.variants) || {};
        const bySku = {};
        Object.keys(variants).forEach((id) => {
          const sku = variants[id].sku;
          if (sku) (bySku[sku] = bySku[sku] || []).push(id);
        });
        // Zubehör-SKUs (Variant.zubehoer / feste Slots) → B2B-Preis auch am Zubehör anzeigen/rechnen.
        const accBySku = {};
        Object.keys(variants).forEach((id) => {
          const acc = variants[id].acc || {};
          Object.keys(acc).forEach((key) => {
            const s = acc[key].sku;
            if (s) (accBySku[s] = accBySku[s] || []).push({ id: id, key: key });
          });
        });
        // Konfigurator-Zubehör hängt nicht mehr in variants[].acc, sondern im Katalog —
        // ohne diesen Zweig verschwänden dessen B2B-Preise still.
        const catBySku = {};
        const catalog = this.data.accCatalog || {};
        Object.keys(catalog).forEach((key) => {
          (catalog[key].vars || []).forEach((v) => {
            if (v.sku) (catBySku[v.sku] = catBySku[v.sku] || []).push(v);
          });
        });
        const skus = Array.from(
          new Set(Object.keys(bySku).concat(Object.keys(accBySku), Object.keys(catBySku)))
        );
        if (!skus.length) return;
        this._b2bApplied = true;

        // Wendet ein prices-Objekt {sku: preis} auf die Matrix an. Idempotent: der
        // ursprüngliche Gast-Packpreis wird pro Variante EINMALIG in v._origPrice
        // gestasht, damit ein zweiter Aufruf (Cache → Revalidate) die Pack/Pro-Einheit-
        // Heuristik nicht auf den bereits überschriebenen B2B-Wert stützt.
        const applyPrices = (prices) => {
          let changed = false;
          Object.keys(prices || {}).forEach((sku) => {
            const price = prices[sku];
            if (price === null || price === undefined) return;
            const cents = vmCents(price);
            if (!isFinite(cents)) return;
            if (bySku[sku]) bySku[sku].forEach((id) => {
              const v = this.data.variants[id];
              // Gast-Packpreis (Rappen) EINMALIG sichern → Basis der Auto-Normalisierung
              // (entscheidet Pack vs. Pro-Einheit), auch bei mehrfachem Apply stabil.
              if (v && v._origPrice === undefined) v._origPrice = (v.price || 0);
              const origPackCents = (v && v._origPrice) || 0;
              const row = this.rowFor(id);
              if (!row) return;
              const up = row.querySelector('.vm-unitprice');
              const q = up ? parseFloat(up.getAttribute('data-suq') || '0') : 0;
              // Abacus-/B2B-Preis ist je Artikel Packpreis ODER bereits Pro-Einheit
              // (kein Signal in der Antwort) → gegen Gast-Packpreis entscheiden und ein
              // KONSISTENTES (Pack, Pro-Einheit)-Paar bilden. Wichtig: die Einzelpreis-Zelle
              // (oben) UND recalc() brauchen den PACKPREIS, nicht den Meterpreis.
              const norm = vmNormalize(cents / 100, origPackCents / 100, q);
              const perUnit = norm.perUnit;
              const packCents = vmCents(norm.packPrice);
              if (v) v.price = packCents;                 // Basis für recalc (Zwischensumme/Gesamt) = Set-Preis
              // ⚠️⚠️ UNGERUNDETEN Packpreis mitführen. B2B-Preise haben Halbrappen
              // (z. B. 12.275). vmCents() rundet auf ganze Rappen (→ 1228), und `qty × 1228`
              // ergab 24.56, während Shopify den Rabatt als PROZENTSATZ auf die ZEILE anwendet
              // und 24.55 verrechnet. Runden gehört ans ENDE der Summe, nie vor die
              // Multiplikation — siehe rowSubtotal().
              if (v) v.priceExact = (parseFloat(norm.packPrice) || 0) * 100;
              const cell = row.querySelector('.vm-col-price .vm-price:not(.vm-price--coming-soon)');
              // Set-Zeilen: Sub-Zeile zeigt den Stückpreis der Bestandteile (B2B-Preis ÷ set_pieces),
              // NICHT den Packpreis. Total-Basis (v.price) bleibt der Set-Preis.
              const setp = up ? parseFloat(up.getAttribute('data-setp') || '0') : 0;
              if (cell) {
                // Set-Stückpreis: abgeleitet → 3 Nachkommastellen (s. snippets/variant-matrix.liquid).
                if (setp > 1) cell.textContent = 'CHF ' + vmUnitPrice(P / setp, setp) + ' / ' + (window.unitLabel ? window.unitLabel('Stück') : 'Stück');
                else cell.textContent = this.formatMoney(packCents);
              }
              if (up && q > 0) {
                const base = up.getAttribute('data-subase') || 'Stück';
                // Heruntergerechnete Einheitspreise: 3 Nachkommastellen (s. money-dec dec: 3).
                up.textContent = 'CHF ' + vmUnitPrice(perUnit, q) + ' / ' + (window.unitLabel ? window.unitLabel(base) : base);
              }
              const cmp = row.querySelector('.vm-col-price .vm-price-compare');
              if (cmp) cmp.remove();
              changed = true;
            });
            // Zubehör-B2B-Preis: Anzeige (+CHF) UND Summen-Basis (acc[key].price) überschreiben.
            // ⚠️ Bugfix 2026-08-11: hier stand bis dahin die ROHE Zuweisung `= cents`, also OHNE
            // die Pack/Pro-Einheit-Normalisierung, die 40 Zeilen darüber für die Hauptvarianten
            // läuft. Konsumenten (rowSubtotal, vm-acc-configurator.js) behandeln `price` aber
            // eindeutig als PACKPREIS in Rappen → Zubehör mit sales_unit_qty > 1 ging um den
            // Faktor suq zu niedrig in Zeilensumme, Gesamtsumme, Badge und Dialog ein.
            if (accBySku[sku]) {
              accBySku[sku].forEach((ref) => {
                const v = this.data.variants[ref.id];
                const a = (v && v.acc && v.acc[ref.key]) || null;
                let accCents = cents;
                if (a) {
                  // Gast-Packpreis EINMALIG sichern → stabile Basis bei Cache→Revalidate.
                  if (a._origPrice === undefined) a._origPrice = (a.price || 0);
                  const accNorm = vmNormalize(cents / 100, (a._origPrice || 0) / 100, a.suq);
                  accCents = vmCents(accNorm.packPrice);
                  a.price = accCents;
                  a.priceExact = (parseFloat(accNorm.packPrice) || 0) * 100;   // s. v.priceExact
                }
                const row = this.rowFor(ref.id);
                const chk = row && row.querySelector('.vm-mp-check[data-acc-key="' + ref.key + '"]');
                const mp = chk && chk.closest('.vm-mp');
                const span = mp && mp.querySelector('.vm-mp__price');
                if (span) span.textContent = '+' + this.formatMoney(accCents);
                changed = true;
              });
            }
            // Konfigurator-Katalog: Preisbasis für Summen, Badge und Dialog-Zellen.
            // Gleiche Normalisierung wie oben — vm-acc-configurator.js teilt `price` selbst
            // nochmals durch suq für die Pro-Einheit-Zeile, erwartet hier also den Packpreis.
            if (catBySku[sku]) {
              catBySku[sku].forEach((v) => {
                if (v._origPrice === undefined) v._origPrice = (v.price || 0);
                v.price = vmCents(vmNormalize(cents / 100, (v._origPrice || 0) / 100, v.suq).packPrice);
                changed = true;
              });
            }
          });
          if (changed) {
            this.recalc();
            this.updateTopPrice();
            if (this.cfg) this.cfg.onPricesUpdated();
          }
        };

        // SWR Schritt 1: persistente Cache-Treffer SOFORT anwenden (kein Flash / kein
        // Lade-Hinweis, wenn alle Preise schon im Cache stehen).
        let fullHit = false;
        if (window.B2BPriceCache) {
          const hits = window.B2BPriceCache.getMany(skus);
          const cachedPrices = {};
          Object.keys(hits).forEach((sku) => { cachedPrices[sku] = hits[sku].price; });
          if (Object.keys(cachedPrices).length) applyPrices(cachedPrices);
          fullHit = skus.every((sku) => Object.prototype.hasOwnProperty.call(cachedPrices, sku));
        }

        // Skeleton-/Shimmer-Zustand statt Listenpreis, solange nicht alle Preise schon
        // aus dem Cache stehen (Top-"Ab"-Preis + Matrix-Preiszellen). Fail-safe: 5 s.
        if (!fullHit) this.setB2bPending(true);
        const self = this;
        const failSafe = setTimeout(function () { self.setB2bPending(false); }, 5000);
        var done = function () { clearTimeout(failSafe); self.setB2bPending(false); };

        // SWR Schritt 2: immer live revalidieren und Cache aktualisieren.
        // In Blöcken à 100 SKUs: mit dem Zubehör-Katalog wächst die &skus[]=-Liste sonst
        // über die vernünftige URL-Länge hinaus. Jeder Block wird sofort angewandt
        // (progressive Anzeige), ein fehlgeschlagener Block blockiert die anderen nicht.
        const CHUNK = 100;
        const chunks = [];
        for (let i = 0; i < skus.length; i += CHUNK) chunks.push(skus.slice(i, i + CHUNK));
        Promise.all(
          chunks.map((part) => {
            let url = cfg.apiBase + '/api/b2b/customer-prices?email=' + encodeURIComponent(cfg.email);
            part.forEach((s) => { url += '&skus[]=' + encodeURIComponent(s); });

            return fetch(url, { headers: { 'X-Api-Key': cfg.apiKey } })
              .then((r) => r.json())
              .then((res) => {
                const prices = (res && res.prices) || {};
                applyPrices(prices);
                if (window.B2BPriceCache) window.B2BPriceCache.setMany(prices, res && res.fallback_level);
              })
              .catch((e) => { if (window.console) console.warn('[B2B] matrix price fetch failed:', e); });
          })
        ).finally(done);
      }

      // Skeleton-/Shimmer auf Top-"Ab"-Preis + Matrix-Preiszellen, solange die
      // B2B-Preise laden (verhindert kurzes Aufblitzen des Listenpreises). Idempotent.
      setB2bPending(on) {
        document.querySelectorAll('.product__info-wrapper .price').forEach((el) => {
          el.classList.toggle('b2b-price-pending', !!on);
        });
        this.querySelectorAll(
          '.vm-col-price .vm-price:not(.vm-price--coming-soon), .vm-unitprice, .vm-cfg-item__price'
        ).forEach((el) => {
          el.classList.toggle('b2b-price-pending', !!on);
        });
      }

      // Setzt den "Ab"-Preis ganz oben (und im Sticky-Cart) auf das Minimum der
      // kundenindividuellen Variantenpreise. b2b-prices.js überlässt der Matrix
      // den Top-Preis (kein Konflikt).
      updateTopPrice() {
        const variants = (this.data && this.data.variants) || {};
        // "Ab"-Preis = GÜNSTIGSTER PRO-EINHEIT-Preis über alle Varianten, NICHT der kleinste
        // Packpreis. Die Verkaufseinheit (VPE, data-suq) variiert je Variante (z. B. Messing
        // vernickelt: M12=100 Stk, M50=6 Stk) → die Variante mit dem kleinsten Packpreis (M50)
        // ist NICHT die pro Stück günstigste (M12). v.price ist der (B2B-)Packpreis in Rappen;
        // pro Einheit = Packpreis ÷ VPE. Ohne VPE (q<=1, z. B. Sets) bleibt es der Variantenpreis.
        // Mirror von price.liquid (pp_min) → deckungsgleich mit Collection-Karte & PDP-Headline.
        let min = null, minBase = 'Stück', minQ = 0;
        Object.keys(variants).forEach((id) => {
          const c = variants[id].price;
          if (typeof c !== 'number' || !isFinite(c) || c <= 0) return;
          const row = this.rowFor(id);
          const up = row && row.querySelector('.vm-unitprice');
          const q = up ? (parseFloat(up.getAttribute('data-suq') || '0') || 0) : 0;
          const perCents = (q > 1) ? (c / q) : c;
          if (min === null || perCents < min) {
            min = perCents;
            minQ = q;
            minBase = (up && up.getAttribute('data-subase')) || 'Stück';
          }
        });
        if (min === null) return;
        // Einheiten-Suffix ("/ Stück", "/ Meter") nur wenn die pro-Einheit günstigste Variante
        // eine VPE hat (q>1) → Top-Headline byte-gleich zur PDP-Inline-Headline
        // ("ab CHF 1.51 / Stück"), damit kein Flackern entsteht, egal welcher Writer zuletzt läuft.
        const hasUnit = minQ > 1;
        const suffix = hasUnit ? (' / ' + (window.unitLabel ? window.unitLabel(minBase) : minBase)) : '';
        const prefix = hasUnit ? (window.fromPrefix || 'ab ') : (window.fromPrefixCap || 'Ab ');
        // S17-DEC (17.08.2026): abgeleitete Einheitspreise dreistellig — formatMoney() rundet auf
        // ganze Rappen (richtig fuer Pack-/Positionspreise, hier aber falsch: der B2B-Kunde sah
        // "ab CHF 0.46 / Meter", der Gast daneben "ab CHF 0.787 / Meter" aus money-dec dec: 3).
        // vmUnitPrice() ist dieselbe Quelle wie Zeile 606 und wie die PDP-Inline-Headline
        // (window.SU.unitPrice) → Top-Headline und Inline-Headline bleiben byte-gleich, kein Flackern.
        // Ohne VPE (q<=1) ist der "Einheitspreis" der Verkaufspreis selbst → weiterhin formatMoney().
        const text = prefix + (hasUnit
          ? 'CHF ' + vmUnitPrice(min / 100, minQ)
          : this.formatMoney(Math.round(Number(min.toPrecision(12))))) + suffix;
        document
          .querySelectorAll('.product__info-wrapper .price .price-item--regular, .product__info-wrapper .price .price-item--last, .sticky_cart .price-item--regular, .sticky_cart .price-item--last')
          .forEach((el) => {
            const s = el.closest('s'); if (s) { s.remove(); return; }  // List-Streichpreis weg
            el.textContent = text;
          });
        // Bei VPE-"Ab"-Preisen die prominente PDP-Headline explizit setzen (Span ggf. anlegen)
        // und als matrix-owned markieren → renderHeadline überschreibt sie danach nicht mehr mit
        // dem Gast-Wert. Für Nicht-VPE-Matrizen (Sets etc.) unverändert: #unit-headline bleibt leer.
        if (hasUnit) {
          const hl = document.querySelector('.product__unit-headline');
          if (hl) {
            hl.setAttribute('data-vm-owned', '1');
            hl.innerHTML = '<span class="price-item price-item--regular">' + text + '</span>';
          }
        }
      }

      // ---- Auswahl / Summen -------------------------------------------------

      rowFor(variantId) {
        return this.querySelector(`.variant-matrix__row[data-variant-id="${variantId}"]`);
      }

      // Zeile von Suche/Filter ausgeblendet? (applyFilters setzt display:none)
      isRowShown(row) {
        return !!row && row.style.display !== 'none';
      }

      // Auswahl-Checkboxen der sichtbaren Zeilen
      visibleBoxes() {
        return Array.from(this.querySelectorAll('.vm-select')).filter((cb) =>
          this.isRowShown(cb.closest('.variant-matrix__row'))
        );
      }

      // Ausgeblendete Zeilen zählen nicht: ihr Haken bleibt erhalten (kommt beim
      // Zurücksetzen des Filters wieder), geht aber weder in Summe noch in den Warenkorb.
      isChecked(variantId) {
        const row = this.rowFor(variantId);
        const cb = row && row.querySelector('.vm-select');
        return !!(cb && cb.checked && this.isRowShown(row));
      }

      // Zubehör-Map der Zeile: { key: {id, price, sku, title} } (aus Daten-Insel)
      accFor(variantId) {
        const v = this.data.variants[variantId];
        return (v && v.acc) || {};
      }

      // Ist das Zubehör <key> für diese Zeile angehakt?
      accChecked(variantId, key) {
        const row = this.rowFor(variantId);
        const cb = row && row.querySelector('.vm-mp-check[data-acc-key="' + key + '"]');
        return !!(cb && cb.checked);
      }

      // Menge eines angehakten Zubehörs: editable → eigener Mengenwähler (min 1),
      // sonst (inherit) → Menge der Schrank-/Gehäusezeile.
      accQty(variantId, key, baseQty) {
        const row = this.rowFor(variantId);
        const cb = row && row.querySelector('.vm-mp-check[data-acc-key="' + key + '"]');
        if (cb && cb.dataset.accQtyMode === 'editable') {
          const inp = row.querySelector('.vm-acc-qty[data-acc-key="' + key + '"]');
          const val = inp ? parseInt(inp.value, 10) : 1;
          return val > 0 ? val : 1;
        }
        return baseQty;
      }

      // Erweitert die Basis-Items (Schrank/Gehäuse) um jedes angehakte Zubehör.
      expandItems(items) {
        const out = [];
        items.forEach((it) => {
          out.push({ id: it.id, quantity: it.quantity });
          const acc = this.accFor(it.id);
          const seenAcc = {}; // dasselbe Zubehör (Variant-ID) je Basiszeile nur einmal
          Object.keys(acc).forEach((key) => {
            const a = acc[key];
            if (a && a.id && a.available !== false && this.accChecked(it.id, key)) {
              if (seenAcc[a.id]) return; // Slot + Liste referenzieren dasselbe → nicht doppeln
              seenAcc[a.id] = true;
              const row = this.rowFor(it.id);
              const labelEl = row && row.querySelector('.vm-mp-check[data-acc-key="' + key + '"]');
              const label =
                (labelEl &&
                  labelEl.closest('.vm-mp') &&
                  labelEl.closest('.vm-mp').querySelector('.vm-mp__text') &&
                  labelEl.closest('.vm-mp').querySelector('.vm-mp__text').textContent.trim()) ||
                key;
              out.push({
                id: Number(a.id),
                quantity: this.accQty(it.id, key, it.quantity),
                properties: { 'Zubehör': label + ' zu ' + (a.sku || '') },
              });
            }
          });
          // Konfigurator-Auswahl derselben Zeile. Nutzt bewusst DENSELBEN seenAcc-Map:
          // referenziert ein Fixslot dieselbe Variante, entsteht keine Dublette.
          const sel = (this.cfgSel && this.cfgSel[it.id]) || {};
          const hostSku = (this.data.variants[it.id] || {}).sku || '';
          Object.keys(sel).forEach((key) => {
            const a = this.data.accCatalog[key];
            const v = this.accVariant(key, sel[key].vid);
            if (!a || !v || v.available === false || !v.price || v.price <= 0) return;
            if (seenAcc[v.id]) return; // Fixslot referenziert dieselbe Variante
            seenAcc[v.id] = true;
            out.push({
              id: Number(v.id),
              quantity: Math.max(1, parseInt(sel[key].qty, 10) || 1),
              properties: { 'Zubehör': (a.title || '') + ' zu ' + hostSku },
            });
          });
        });
        return out;
      }

      qtyFor(variantId) {
        const row = this.rowFor(variantId);
        const inp = row && row.querySelector('.quantity__input');
        let val = inp ? parseInt(inp.value, 10) : 0;
        // Bestandsdeckel (UX): nie mehr als der getrackte Lagerbestand anfordern.
        // Serverseitig erzwingt Shopify die Grenze zusätzlich (422 → showError).
        if (inp && inp.dataset.invMax) {
          const max = parseInt(inp.dataset.invMax, 10);
          if (max > 0 && val > max) {
            val = max;
            inp.value = max;
          }
        }
        return val > 0 ? val : 0;
      }

      // Ändert der Nutzer die Menge einer Zeile, wird die Variante automatisch
      // angehakt (nie automatisch abgewählt — Abwählen bleibt manuell).
      onQtyChange(input) {
        const row = input.closest('.variant-matrix__row');
        if (row) {
          const cb = row.querySelector('.vm-select');
          if (cb && !cb.checked) cb.checked = true;
        }
        this.onSelectionChange();
      }

      selectedItems() {
        return Object.keys(this.data.variants)
          .filter((id) => this.isChecked(id) && this.data.variants[id] && this.data.variants[id].available)
          .map((id) => ({ id: Number(id), quantity: this.qtyFor(id) }))
          .filter((it) => it.quantity > 0);
      }

      formatMoney(cents) {
        return 'CHF ' + (cents / 100).toFixed(2);
      }

      // Zeilensumme in Rappen: Variante × Menge + angehakte Fixslots + Konfigurator-Auswahl.
      // Einzige Quelle für die Zwischensumme der Tabelle UND den "Gesamt (Zeile)"-Wert im
      // Konfigurator — sonst zeigte der Dialog eine andere Zahl als die Zeile dahinter
      // (die vorausgewählte Montageplatte fehlte).
      rowSubtotal(id, qtyOverride) {
        const v = this.data.variants[id];
        if (!v) return 0;
        const qty = typeof qtyOverride === 'number' ? qtyOverride : this.qtyFor(id);
        // ⚠️ Mit den UNGERUNDETEN Rappen rechnen und erst die fertige Summe runden — sonst
        // multipliziert sich der Rundungsfehler jedes Halbrappen-Preises mit der Menge und
        // die Anzeige weicht von der Shopify-Abrechnung ab (24.56 statt 24.55).
        const exact = (o) => (o && o.priceExact !== undefined ? o.priceExact : ((o && o.price) || 0));
        let sub = qty * exact(v);
        const acc = this.accFor(id);
        const seen = {};
        Object.keys(acc).forEach((key) => {
          const a = acc[key];
          if (a && a.id && a.available !== false && this.accChecked(id, key)) {
            seen[a.id] = true;
            sub += this.accQty(id, key, qty) * exact(a);
          }
        });
        // Konfigurator-Zubehör der Zeile (Dedup gegen die Fixslots wie in expandItems).
        const sel = (this.cfgSel && this.cfgSel[id]) || {};
        Object.keys(sel).forEach((key) => {
          const v = this.accVariant(key, sel[key].vid);
          if (!v || v.available === false || seen[v.id]) return;
          sub += Math.max(1, parseInt(sel[key].qty, 10) || 1) * exact(v);
        });

        // Erst hier runden: eine Rundung auf die fertige Zeilensumme, nicht je Bestandteil.
        return Math.round(Number(sub.toPrecision(12)));
      }

      // Zubehör, das ZUSÄTZLICH zur Vorauswahl der Zeile gewählt wurde. Die ab Werk
      // angehakten Fixslots (Seitenwand, Sockel, Montageplatte) zählen bewusst NICHT mit:
      // sie stehen ohnehin sichtbar in der Zeile, im Badge wären sie nur Rauschen.
      cfgCount(id) {
        return Object.keys((this.cfgSel && this.cfgSel[id]) || {}).length;
      }

      cfgSurcharge(id) {
        const sel = (this.cfgSel && this.cfgSel[id]) || {};

        return Object.keys(sel).reduce((s, key) => {
          const v = this.accVariant(key, sel[key].vid);

          return s + (v ? v.price || 0 : 0) * Math.max(1, parseInt(sel[key].qty, 10) || 1);
        }, 0);
      }

      // Badge am Auslöser. Liegt in der Matrix und nicht im Konfigurator, damit es auch
      // ohne geöffneten Dialog mitläuft (z. B. wenn direkt in der Zeile gehakt wird).
      syncCfgBadge(id) {
        const row = this.rowFor(id);
        const badge = row && row.querySelector('.vm-cfg-badge');
        if (!badge) return;
        const n = this.cfgCount(id);
        if (!n) {
          badge.hidden = true;
          badge.textContent = '';

          return;
        }
        badge.hidden = false;
        badge.textContent = n + ' · +' + this.formatMoney(this.cfgSurcharge(id));
        const tpl = (this.querySelector('.vm-cfg-modal') || { dataset: {} }).dataset.i18nBadge;
        if (tpl) badge.setAttribute('aria-label', tpl.replace('@@', n));
      }

      // Gewählte Ausführung eines Konfigurator-Zubehörs (Standard: erste bepreiste verfügbare).
      accVariant(key, vid) {
        const a = this.data.accCatalog[key];
        if (!a || !a.vars || !a.vars.length) return null;
        if (vid) {
          const hit = a.vars.find((v) => String(v.id) === String(vid));
          if (hit) return hit;
        }

        return a.vars.find((v) => v.available !== false && v.price > 0) || a.vars[0];
      }

      recalc() {
        let grand = 0;
        Object.keys(this.data.variants).forEach((id) => {
          const checked = this.isChecked(id);
          const qty = this.qtyFor(id);
          // Meterware: Gesamtlänge der Zeile („= 12 m") an der Mengenspalte nachziehen.
          const row = this.rowFor(id);
          if (row && window.SU) window.SU.refreshUnitTotals(row, checked ? qty : 0);
          const cell = this.querySelector(`.vm-subtotal[data-variant-id="${id}"]`);
          if (checked && qty > 0) {
            const sub = this.rowSubtotal(id, qty);
            grand += sub;
            if (cell) cell.textContent = this.formatMoney(sub);
          } else if (cell) {
            cell.textContent = '';
          }
        });
        const formatted = this.formatMoney(grand);
        (this.grandEls || []).forEach((el) => {
          el.textContent = formatted;
        });
      }

      onSelectionChange() {
        const boxes = Array.from(this.querySelectorAll('.vm-select'));
        // Zeilen-Highlight
        boxes.forEach((cb) => {
          const row = cb.closest('.variant-matrix__row');
          if (row) row.classList.toggle('is-selected', cb.checked);
        });
        // "Alle auswählen"-Status (auch indeterminate) und Sammel-Buttons beziehen
        // sich nur auf die sichtbaren Zeilen.
        const shown = this.visibleBoxes();
        const shownChecked = shown.filter((cb) => cb.checked);
        if (this.selectAll) {
          this.selectAll.checked = shown.length > 0 && shownChecked.length === shown.length;
          this.selectAll.indeterminate = shownChecked.length > 0 && shownChecked.length < shown.length;
        }
        // Sammel-Buttons aktiv/inaktiv
        this.addAllBtns.forEach((btn) =>
          btn.setAttribute('aria-disabled', shownChecked.length ? 'false' : 'true')
        );
        this.recalc();
        this.querySelectorAll('.vm-cfg-open').forEach((b) => this.syncCfgBadge(b.dataset.variantId));
      }

      // ---- Suche + Attribut-Filter -----------------------------------------

      activeFilters() {
        const out = {};
        (this.filterSelects || []).forEach((sel) => {
          if (sel.value) out[sel.dataset.filterKey] = sel.value;
        });
        return out;
      }

      applyFilters() {
        const q = (this.searchTerm || '').trim().toLowerCase();
        const filters = this.activeFilters();
        const keys = Object.keys(filters);
        let visible = 0;
        this.querySelectorAll('.variant-matrix__row').forEach((row) => {
          let match = !q || (row.dataset.search || '').indexOf(q) !== -1;
          if (match && keys.length) {
            let f = {};
            try {
              f = JSON.parse(row.dataset.filters || '{}');
            } catch (e) {
              f = {};
            }
            for (let i = 0; i < keys.length; i++) {
              const rv = f[keys[i]] == null ? '' : f[keys[i]];
              if (String(rv) !== String(filters[keys[i]])) {
                match = false;
                break;
              }
            }
          }
          row.style.display = match ? '' : 'none';
          if (match) visible++;
        });
        if (this.noResultsEl) this.noResultsEl.hidden = visible !== 0;
        if (this.resetBtn) this.resetBtn.hidden = !(q || keys.length);
        // Sichtbarkeit bestimmt, was zählt → Kopf-Haken, Sammel-Buttons und Summen nachziehen.
        this.onSelectionChange();
      }

      resetFilters() {
        this.searchTerm = '';
        const searchInput = this.querySelector('.variant-matrix__search-input');
        if (searchInput) searchInput.value = '';
        (this.filterSelects || []).forEach((sel) => {
          sel.value = '';
        });
        this.applyFilters();
      }

      // Rückwärtskompatibilität (Suche per Term, falls extern aufgerufen)
      filterRows(term) {
        this.searchTerm = term || '';
        this.applyFilters();
      }

      // ---- Warenkorb --------------------------------------------------------

      addSingle(btn) {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const id = btn.dataset.variantId;
        const qty = this.qtyFor(id);
        if (!qty) {
          this.showError(window.customStrings.vmChooseQty);
          return;
        }
        this.post(this.expandItems([{ id: Number(id), quantity: qty }]), btn);
      }

      addSelected(btn) {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const items = this.selectedItems();
        if (!items.length) {
          this.showError(window.customStrings.vmSelectVariant);
          return;
        }
        this.post(this.expandItems(items), btn);
      }

      buildSections() {
        return this.cart && this.cart.getSectionsToRender
          ? this.cart.getSectionsToRender().map((s) => s.id)
          : [];
      }

      // Frische Section-HTMLs für Warenkorb-Drawer + Icon-Bubble über die
      // Section-Rendering-API holen (Antwort keyed nach Section-ID) — gibt den
      // ECHTEN Server-Warenkorb wieder, unabhängig davon, was /cart/add.js im
      // Deckelungsfall (422) zurückliefert.
      fetchCartSections() {
        const ids = this.buildSections();
        if (!ids.length) return Promise.reject(new Error('no sections'));
        const base = (window.routes && window.routes.cart_url) || window.localizeUrl('/cart');
        const url = `${base}?sections=${encodeURIComponent(ids.join(','))}`;
        return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((r) => {
          if (!r.ok) throw new Error('sections fetch failed');
          return r.json();
        });
      }

      post(items, btn) {
        this.clearError();
        this.setLoading(btn, true);

        const body = {
          items: items,
          sections: this.buildSections(),
          sections_url: window.location.pathname,
        };

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(body);
        if (this.cart && this.cart.setActiveElement) this.cart.setActiveElement(document.activeElement);

        fetch(`${this.cartAddUrl}.js`, config)
          .then((response) => response.json().then((json) => ({ ok: response.ok, json: json })))
          .then(({ ok, json }) => {
            // Sauberer Erfolg: /cart/add.js liefert die angeforderten sections mit.
            if (ok && !json.status) {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'variant-matrix', cartData: json });
              if (this.cart && this.cart.renderContents) {
                if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                this.cart.renderContents(json);
              } else {
                window.location = (window.routes && window.routes.cart_url) || window.localizeUrl('/cart');
              }
              return;
            }

            // Mehrdeutige Antwort — insbesondere HTTP 422 bei Verfügbarkeits-Deckelung:
            // Shopify nimmt die gedeckelte Menge TROTZDEM in den Warenkorb auf, liefert
            // aber KEINE sections und ein status/description-Feld. Der Antwort NICHT
            // vertrauen (frühere Annahme „items-Array ist atomar" stimmt für den Cap nicht),
            // sondern den echten Warenkorb-Zustand nachladen und rendern — sonst bleibt die
            // UI stale (Artikel erst nach Reload sichtbar). Danach die Server-Meldung als
            // freundlichen Hinweis zeigen (nicht als harten Fehler).
            const info = json.description || json.message || '';
            if (!this.cart || !this.cart.renderContents) {
              window.location = (window.routes && window.routes.cart_url) || window.localizeUrl('/cart');
              return;
            }
            return this.fetchCartSections()
              .then((sections) => {
                if (this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
                this.cart.renderContents({ sections: sections });
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'variant-matrix' });
                if (info) this.showNotice(info);
                else this.showError(window.customStrings.addFailed);
              })
              .catch(() => {
                // Sections nicht ladbar → Voll-Reload, nie inkonsistent zurücklassen.
                window.location = (window.routes && window.routes.cart_url) || window.localizeUrl('/cart');
              });
          })
          .catch((e) => {
            console.error(e);
            this.showError('Netzwerkfehler. Bitte erneut versuchen.');
          })
          .finally(() => this.setLoading(btn, false));
      }

      // ---- UI-Helfer --------------------------------------------------------

      setLoading(btn, on) {
        btn.classList.toggle('loading', on);
        const isAddAll = this.addAllBtns && this.addAllBtns.indexOf(btn) !== -1;
        // aria-disabled beim Sammel-Button nicht überschreiben, wenn keine Auswahl.
        // Während des Ladens werden ALLE Sammel-Buttons (oben + Footer) gesperrt.
        if (on) {
          if (isAddAll) {
            this.addAllBtns.forEach((b) => b.setAttribute('aria-disabled', 'true'));
          } else {
            btn.setAttribute('aria-disabled', 'true');
          }
        } else if (isAddAll) {
          this.onSelectionChange();
        } else {
          btn.setAttribute('aria-disabled', 'false');
        }
        const sp = btn.querySelector('.loading__spinner');
        if (sp) sp.classList.toggle('hidden', !on);
      }

      showError(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.classList.remove('variant-matrix__error--notice');
        this.errorEl.setAttribute('role', 'alert');
        this.errorEl.hidden = false;
      }

      // Freundlicher Hinweis (z. B. „nur N aufgrund Verfügbarkeit hinzugefügt"):
      // gleiche Zeile, aber neutraler Stil + role="status" statt Alarm.
      showNotice(msg) {
        if (!this.errorEl) return;
        this.errorEl.textContent = msg;
        this.errorEl.classList.add('variant-matrix__error--notice');
        this.errorEl.setAttribute('role', 'status');
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('variant-matrix__error--notice');
        this.errorEl.setAttribute('role', 'alert');
      }
    }
  );
}

/* CTA-Buttons (Info-Spalte / Sticky-Balken): zur Variantentabelle springen
   und sie kurz hervorheben, statt direkt in den Warenkorb zu legen. */
if (!window.__vmJumpBound) {
  window.__vmJumpBound = true;
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-vm-target]');
    if (!btn) return;
    const el = document.getElementById(btn.getAttribute('data-vm-target'));
    if (!el) return;
    e.preventDefault();

    // Zielposition: kurze Tabellen mittig anvisieren (sonst wirkt es "zu weit
    // gescrollt"), lange Tabellen oben mit etwas Abstand.
    const rect = el.getBoundingClientRect();
    const elTop = rect.top + window.pageYOffset;
    const vh = window.innerHeight;
    let y;
    if (rect.height < vh - 120) {
      y = elTop - Math.max(80, (vh - rect.height) / 2);
    } else {
      y = elTop - 90;
    }
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });

    el.classList.remove('vm-flash');
    // reflow erzwingen, damit die Animation bei mehrfachem Klick neu startet
    void el.offsetWidth;
    el.classList.add('vm-flash');
    setTimeout(function () {
      el.classList.remove('vm-flash');
    }, 1600);
  });
}
